import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

interface FormDialogProps {
  open: boolean;
  title: string;
  onClose: () => void;
  onSubmit: () => void;
  submitting?: boolean;
  /** i18n key of a server error to show above the fields. */
  errorKey?: string | null;
  children: ReactNode;
  submitLabel?: string;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg';
}

export function FormDialog({
  open,
  title,
  onClose,
  onSubmit,
  submitting = false,
  errorKey,
  children,
  submitLabel,
  maxWidth = 'sm',
}: FormDialogProps) {
  const { t } = useTranslation();

  return (
    <Dialog
      open={open}
      // Closing mid-save would leave the user unsure whether it went through.
      onClose={submitting ? undefined : onClose}
      maxWidth={maxWidth}
      fullWidth
    >
      <form
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit();
        }}
        noValidate
      >
        <DialogTitle>{title}</DialogTitle>
        <DialogContent dividers>
          {errorKey && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {t(errorKey)}
            </Alert>
          )}
          {children}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={submitting}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {submitLabel ?? t('common.save')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
